import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { IDriver } from "dataModels/contract";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { Button, Output, Table } from "@ds/ds-snam/react";
import { getDriver, downloadInfo, downloadDocument, deleteEntity, changeStatus, putDocument } from "net/contracts";
import { BadgeStatus, DATE_FORMAT, EntityType, DocumentType, RoutePath, UICommand, IAppUserProfile, DsLayout, FormMode, ULTRA_LATE_DATE, ResponseStatus, ResponseCode } from "shared/constants";
import { Container, Row, Col } from "react-grid-system";
import { Textlink } from "@ds/ds-snam/react";
import "views/contractView/styles/driverDataStyles.css";
import "views/contractView/styles/contractDataStyles.css";
import driverIcon from "shared/assets/icons/Autisti.svg";
import SimpleTable, { SimpleTableType } from "components/SimpleTable";
import { IDocument } from "dataModels/contract";
import moment from "moment";
import { saveAs } from 'file-saver';
import { showNotification, hideNotification } from "state/reducer/notificationSlice";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import ConfirmPopUp from "components/ConfirmPopUp";
import ApproveButtons from "components/ApproveButtons";
import DialogMessage from "components/DialogMessage";
import DsBadge from "components/DsBadge";
import useAppUser from "hooks/useAppUser";
import { RootState } from "state/appStore";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import DriverFormReview from "./driverFormReview";
import DriverInfo from "./driverInfo";
import { ITableCell, ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { getTranslationPath } from "./vehicleData";
import { getDSTableShaderElementsForFilterColumn,getDSTableSharedElements, getTableCellsHeaderSimple  } from "shared/utils/dsComponentUtils";
import { documentTableLegend } from "../shared/listUtils";

const DriverData: React.FC = (): JSX.Element => {
    const { id } = useParams();
    const [driverData, setDriverData] = useState<IDriver>();
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const appUserProfile: IAppUserProfile | undefined = useAppUser()?.userProfile;
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    const { t } = useTranslation(['contract', 'app']);
    const navigate = useNavigate();
    const [command, setCommand] = useState<UICommand | undefined>();

    const dispatch = useDispatch();
    const loadDriverData = useCallback(() => {
        dispatch(requestPending());
        getDriver(id!)
            .then((response) => {
                //console.log(response.data)
                setDriverData(response.data)
            })
            .catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [id, dispatch]);

    useEffect(() => {
        if (id)
            loadDriverData();
    }, [id, loadDriverData]);

    const existExpiredDocument = () => { 
        const today = new Date();
        return driverData!.documents.some(doc => 
            doc.expire !== null && new Date(doc.expire * 1000) < today
        );
    }
    const getDriverStatusBadge = () => {

        let notificationStatus: string = "";
        let statusMessage: string = "";

        if (driverData?.statusComplete === BadgeStatus.APPROVED) {
            notificationStatus = "confirm";
            statusMessage = t('drivers.driverData.statusBadgeMessage.1');
        } else if (driverData?.statusComplete === BadgeStatus.TOAPPROVE) {
            notificationStatus = "warning";
            statusMessage = t('drivers.driverData.statusBadgeMessage.0')+t('vehicle.vehicleData.statusBadgeMessage.5');
        } else if (driverData?.statusComplete === BadgeStatus.TO_CREDIT){
            notificationStatus = "warning";
            statusMessage = t('drivers.driverData.statusBadgeMessage.0')+t('vehicle.vehicleData.statusBadgeMessage.4');
        }else if (driverData?.statusComplete === BadgeStatus.NOTAPPROVED) {
            notificationStatus = "error";
            statusMessage = t('drivers.driverData.statusBadgeMessage.2');
        } else if (driverData?.statusComplete === BadgeStatus.NOT_CREDITED) {
            notificationStatus = "error";
            statusMessage = t('drivers.driverData.statusBadgeMessage.3');
        } else if (driverData?.statusComplete === BadgeStatus.CREDITED) {
            notificationStatus = "confirm";
            statusMessage = t('drivers.driverData.statusBadgeMessage.6');
        }else if ( (driverData?.statusComplete === BadgeStatus.EXPIRED) && (existExpiredDocument()===true)) {
            notificationStatus = "error";
            statusMessage = t('drivers.driverData.statusBadgeMessage.7');
        }else if ( (driverData?.statusComplete === BadgeStatus.EXPIRED) && (existExpiredDocument()===false)) {
            notificationStatus = "error";
            statusMessage = t('drivers.driverData.statusBadgeMessage.8');
        }

        return <div style={{ display: "flex", width: "163px", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}><ds-notification-badge
            notification-status={notificationStatus}
            badge-variant="default"
            badge-number="0"
            badge-aria-label="Badge aria label"
            is-small={true}
            background-color=""
        ></ds-notification-badge>
            <span className="statusMessage">
                {statusMessage}
            </span></div>

    }

    const getBadgeStatus = (status: BadgeStatus) => {
        let notificationStatus = "";

        if (status === BadgeStatus.EXPIRED) {
            notificationStatus = 'error'
        } else if (status === BadgeStatus.VALID) {
            notificationStatus = 'confirm'
        }

        return notificationStatus;
    }

    const tableCols = (() => {
        //return ["Tipologia documento", "Data fine validità", "", ""];
        return t("drivers.driverData.fileTable.tableColumns", { ns: 'contract', returnObjects: true }) as string[];
    })();

    const tableRows = (start: number, end?: number) => {
        return driverData?.documents.slice(start, end ? end : undefined).map((document: IDocument, index) => {
            return (
                <tr key={document.name}>
                    <td>
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {document.status!==undefined && <DsBadge notificationStatus={getBadgeStatus(document.status)} />}
                            {t('drivers.driverForm.sections.fileInput.title.' + (index + start))}
                        </span>
                    </td>

                    <td>
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {document.expire ? parseDateForDS(moment.unix(document.expire).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1) : ""}
                        </span>
                    </td>


                    <td style={{ textAlign: "center" }}>
                        <Textlink
                            textlinkText={t('general.buttons.modify', { ns: 'app' })}//"Modifica"
                            textlinkTextSize="SMALL"
                            isRouterLink={true}
                            onClickLink={() => {
                                navigate(RoutePath.CONTRACTS_EDIT_DOCUMENT + "?entityId=" + id + "&entityType=" + EntityType.DRIVER + "&documentType=" + document.type);
                            }}
                            hidden={!appUserProfile?.editEntityPolicy.includes(EntityType.DRIVER)}
                        />
                    </td>

                    <td>
                        <Button
                            buttonType="button"
                            buttonUIVariant="tertiary"
                            buttonSize="medium"
                            buttonLabel=""
                            buttonIcon="icon-download"
                            isDisabled={false}
                            buttonAriaLabel="Aria text here"
                            onClickEvent={(event: Event) => {
                                downloadDriverDocument(document.type as DocumentType);
                            }}
                        ></Button>
                    </td>

                </tr>
            )
        });
    }



    const downloadDriverInfo = () => {
        //downloadTransporterData(id!)
        downloadInfo(id!, EntityType.DRIVER)
            .then((response) => {
                let file = new File([response.data], "driver_" + id! + ".pdf");
                saveAs(file);
            })
            .catch((error) => {
                console.log(error)
            });
    }

    const downloadDriverDocument = (documentType: DocumentType) => {
        downloadDocument(id!, EntityType.DRIVER, documentType)
            .then(response => {
                console.log(response)

                if(response.data.type === 'application/force-download'){
                    let file = new File([response.data], "driver_" + documentType + "_" + id! + ".pdf");
                    saveAs(file);
                }else{
                    let popUpAction: INotificationAction;
                    popUpAction = {
                        show: true,
                        title: 'Warning',
                        description: "Blob non trovato.",
                        status: 'warning',
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    dispatch(showNotification(popUpAction)); 
                }
            })
            .catch(error => {
                let popUpAction: INotificationAction;
                    popUpAction = {
                        show: true,
                        title: 'Warning',
                        description: "Blob non trovato.",
                        status: 'warning',
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    dispatch(showNotification(popUpAction)); 
                console.log(error);
            })
    }

    const deleteDriver = useCallback(() => {
        const popupAction: INotificationAction = {
            show: true,
            title: t(`drivers.driverData.successToast.title`),//"Autista eliminato correttamente",
            description: t(`drivers.driverData.successToast.description`),//"Il profilo dell'autista è eliminato correttamente in piattaforma",//t(`customer.customerForm.successToast.${translationField}.subTitle`),
            status: "confirm",
            onClose: (event: Event) => {
                dispatch(hideNotification());
            }
        };

        dispatch(requestPending());
        deleteEntity(id!, EntityType.DRIVER)
            .then((response) => {
                dispatch(showNotification(popupAction));
                navigate(RoutePath.CONTRACTS_DRIVER_INFO);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                dispatch(requestPristine());
            })
    }, [id, dispatch, navigate, t]);

    const changeEntityStatus = (status: BadgeStatus, message?: string) => {
        const translationPath = getTranslationPath(status);
        if (driverData) {
            changeStatus(driverData?.driverId, EntityType.DRIVER, status, (message ? message : ''))
                .then((response) => {
                    let popUpAction: INotificationAction;
                    popUpAction = {
                        show: true,
                        title: t("drivers.driverData.approvalSuccessToast." + translationPath + ".title"),//'Veicolo approvato',
                        description: t("drivers.driverData.approvalSuccessToast." + translationPath + ".description"),//"Il Veicolo è stato approvato correttamente in piattaforma",
                        status: (status === BadgeStatus.APPROVED || status === BadgeStatus.CREDITED) ? "confirm" : "error",
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    setCommand(undefined);
                    dispatch(showNotification(popUpAction));
                    loadDriverData();
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    dispatch(requestPristine());
                })
        }

    }


    //da qui
    const getTableRows = (startIndex: number, endIndex: number): ITableRow[] => {
        let myRetArr: ITableRow[] = [];
        
        if (driverData?.documents) {
            for (let i = startIndex; i < endIndex; i++) {
                let tempObj: ITableRow = {
                    tableCells: [
                        { 
                            type: "text-badge", 
                            text: t(`drivers.driverForm.sections.fileInput.title.${i}`),
                            badgeStatus: driverData.documents[i] 
                                ?   
                                ( driverData.documents[i].name !== DocumentType.EMPTY_FILE ?
                                    (
                                        driverData.documents[i].status === BadgeStatus.VALID ? 
                                            "confirm" 
                                        : 
                                            "error"
                                    ) 
                                :
                                    "warning"
                                )
                                : "error"
                        },
                        { 
                            type: "text", 
                            text: driverData.documents[i]?.expire && /*driverData.documents[i].expire !== ULTRA_LATE_DATE*/ moment.unix(driverData.documents[i].expire as number).toDate().getFullYear()!==moment.unix(ULTRA_LATE_DATE as number).toDate().getFullYear()
                                ? parseDateForDS(
                                    moment.unix(driverData.documents[i].expire as number).format(DATE_FORMAT), 
                                    IParseDateFormatType.DATE_FORMAT1
                                  ) 
                                : ""
                        },
                        { 
                            type: "text", 
                            text: driverData.documents[i]?.lastUpdate
                        },
                        { 
                            type: "text", 
                            text: driverData.documents[i]?.lastUpdateUser
                        },
                    ],
                    //rightIcon: "icon-download",
                    rightIconMenu:true,
                    "rightIconMenuData":[
                        {
                            "actionLabel": t("general.buttons.modify",{ ns: "app" }),
                            "actionAriaLabel": t("general.buttons.modify",{ ns: "app" }), //EDIT
                            "actionDisabled": !appUserProfile?.editEntityPolicy.includes(EntityType.DRIVER),
                        },
                        {
                            "actionLabel": t("general.buttons.download_short",{ ns: "app" }),
                            "actionAriaLabel": t("general.buttons.download_short",{ ns: "app" }), //DOWNLOAD
                            "actionDisabled": false,
                        },
                        ...(
                            (driverData.documents[i].type === "fiscalCode"||driverData.documents[i].type === "identityCard")
                        ? [{
                            "actionLabel": t("general.buttons.delete_document", { ns: "app" }), //  delete document
                            "actionAriaLabel": t("general.buttons.delete_document", { ns: "app" }),
                            "actionDisabled": false,
                        }]
                        : [])
                    ]
                };
                myRetArr.push(tempObj);
            }
        }
        return myRetArr;
    };
    
    // Funzioni specifiche che richiamano quella generica
    const getTableRowsCert = (): ITableRow[] => getTableRows(4, 7);
    const getTableRowsDocs = (): ITableRow[] => getTableRows(0, 4);
    //a qui



    const myTableConfigCert: ITableConfig = {
        ...getDSTableSharedElements(),
        tableHeader: {
            
            tableCellsHeader: [
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.0", { ns: 'contract' }) as string,"col1","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.1", { ns: 'contract' }) as string,"col2","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.2", { ns: 'contract' }) as string,"col3","25%","left"),
                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.3", { ns: 'contract' }) as string,"col4","25%","left")
            ],
            hasSearchBox: true,
        },
        tableRows: getTableRowsCert()
    };
    const myTableConfigDocs: ITableConfig = {
                        ...getDSTableSharedElements(),
                        tableHeader: {
                            
                            tableCellsHeader: [
                                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.0", { ns: 'contract' }) as string,"col1","25%","left"),
                                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.1", { ns: 'contract' }) as string,"col2","25%","left"),
                                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.2", { ns: 'contract' }) as string,"col3","25%","left"),
                                getTableCellsHeaderSimple(t("drivers.driverData.fileTable.tableColumns.3", { ns: 'contract' }) as string,"col4","25%","left")

                            ],
                            hasSearchBox: true,
                        },
                        tableRows: getTableRowsDocs()
    };

    const deleteSingleDocument = (entityId: string,entityType: EntityType,documentType: string | undefined,document: IDocument | undefined) => {
            let popUpAction: INotificationAction;
            dispatch(requestPending());
            const translationEntity = "drivers";
            putDocument(entityId!, entityType!, document!)
                .then(response => {
                    const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? "Warning": t(`${translationEntity}.documentEdit.successToast.title`));
                    const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t(`${translationEntity}.documentEdit.successToast.subTitle`);
                    const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                                    
                    popUpAction = {
                        show: true,
                        title: popUpTitle,
                        description: popUpDescription,
                        status: popUpStatus,
                        onClose: (event: Event) => {
                            dispatch(hideNotification());
                        }
                    };
                    loadDriverData();
                    dispatch(showNotification(popUpAction));
                }).catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    dispatch(requestPristine())
                });
                //eslint-disable-next-line
    }


    return (<div>
        <Row>
            <Col>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                        <p className="pageTitle" style={{ marginTop: 0 }}>
                            {driverData?.firstName} {driverData?.lastName}
                        </p>
                        {/*{driverData?.status && driverData?.status === BadgeStatus.TOAPPROVE && <p className="pageSubTitle" style={{marginTop:'-20px'}}>
                            {t('drivers.driverData.statusMessage.0')}
                        </p>}
                        {driverData?.status && driverData?.status === BadgeStatus.TO_CREDIT && <p className="pageSubTitle" style={{marginTop:'-20px'}}>
                            {t('drivers.driverData.statusMessage.1')}
                        </p>}*/}
                    </div>
                    <div>
                        {getDriverStatusBadge()}
                    </div>
                </div>
            </Col>
        </Row>

        <Row>
            <Col>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    {appUserProfile?.deleteEntityPolicy.includes(EntityType.DRIVER) ?
                        <Textlink
                            textlinkText={t('general.buttons.removeElement', { ns: 'app', what: t('drivers.driverData.removeButton') })}//"Elimina autista"
                            textlinkTextSize="SMALL"
                            textlinkAriaLabel="aria-Textlink"
                            isRouterLink={true}
                            onClickLink={() => {
                                setShowConfirm(true);
                            }}
                        /> : <div></div>
                    }

                    {driverData && appUserProfile?.approveEntityPolicy.includes(EntityType.DRIVER) &&
                        <div style={{ display: "flex", gap: "16px" }}>
                            <ApproveButtons
                                currentStatus={driverData?.statusComplete}
                                entityType={EntityType.DRIVER}
                                onClick={(command: UICommand) => {
                                    switch (command) {
                                        case UICommand.APPROVE:
                                            changeEntityStatus(BadgeStatus.APPROVED);
                                            break;
                                        case UICommand.CREDIT:
                                            changeEntityStatus(BadgeStatus.CREDITED);
                                            break;
                                        case UICommand.REJECT:
                                        case UICommand.REJECT_CREDIT:
                                            setCommand(command);
                                            break;
                                    }
                                }}
                            />
                        </div>
                    }
                </div>
            </Col>
        </Row>

        <Row>
            <Col>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px",marginTop:'24px' }}>
                        {/*<ds-avatar avatar-type="icon" avatar-size="medium"></ds-avatar>*/}
                        <div style={{ backgroundColor: "var(--interaction-active)", width: "64px", height: "64px", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <img alt="" src={driverIcon} style={{ width: "32px", height: "32px" }} />
                        </div>
                        <div className="avatarNotes--driver">
                            <div>
                                <span>
                                    {/*Nome*/}
                                    {t('drivers.driverData.avatar.label1')}
                                </span>
                                <span> {driverData?.firstName}</span>
                            </div>
                            <div>
                                <span>
                                    {/*Cognome*/}
                                    {t('drivers.driverData.avatar.label2')}
                                </span>
                                <span> {driverData?.lastName}</span>
                            </div>
                        </div>
                    </div>
                    {driverData && <DriverInfo formData={driverData}/>}
                    <div className="driverButtonContainer">
                        {appUserProfile?.editEntityPolicy.includes(EntityType.DRIVER) &&
                            <Button
                                buttonType="button"
                                buttonUIVariant="secondary"
                                buttonSize="medium"
                                buttonLabel={t('general.buttons.edit', { ns: 'app' })}//'Modifica informazioni'
                                buttonIcon=""
                                isDisabled={false}
                                buttonAriaLabel="modify info button"
                                onClick={() => {
                                    navigate(RoutePath.CONTRACTS_EDIT_DRIVER + "/" + id);
                                }}
                            ></Button>
                        }
                        <Button
                            buttonType="button"
                            buttonUIVariant="primary"
                            buttonSize="medium"
                            buttonLabel={t('general.buttons.download', { ns: 'app' })}//'Scarica informazioni'
                            buttonIcon=""
                            isDisabled={false}
                            buttonAriaLabel="download info button"
                            onClick={downloadDriverInfo}
                        ></Button>
                    </div>
                {/*<div className="driverDataContainer">

                    


                    <div className="driverDataBox">
                        <div className="driverDataColumn">
                                <Output
                                    outputTitle={t('drivers.driverData.headers.0')}
                                    outputText={driverData?.birthDate ? parseDateForDS(moment.unix(driverData.birthDate).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1) : ''}
                                ></Output>
                                <Output
                                    outputText={driverData?.birthPlace}
                                ></Output>
                        </div>
                        <div className="driverDataColumn">
                                <Output
                                    outputTitle={t('drivers.driverData.headers.1')}
                                    outputText={driverData?.address+" "+driverData?.streetNumber}
                                ></Output>
                                <Output
                                    outputText={(driverData?.postalCode) +" "+driverData?.city+" "+driverData?.state}
                                ></Output>

                        </div>
                        <div className="driverDataColumn">
                                <Output
                                    outputTitle={t('drivers.driverData.headers.2')}
                                    outputText={driverData?.documentType}
                                ></Output>
                                <Output
                                    outputText={driverData?.documentNumber}
                                ></Output>

                        </div>
                        {driverData?.cardCode!==undefined && <div className="driverCardCodeColumn">
                                <Output
                                    outputTitle={"Identity Card"} //code card
                                    outputText={driverData?.cardCode}
                                ></Output>
                        </div>}
                    </div>
                    

                    
                </div>*/}


            </Col>
        </Row>

        <Row>
            <Col>
                <div className="documentTableTitle--contract">
                    {/*Documenti*/}
                    {t('drivers.driverData.fileTable.title.0')}
                </div>
                <div className="listTableContainer" style={{marginTop: "24px" }}>
                    {/*<SimpleTable id="simpleTable_1" columns={tableCols} rows={tableRows(0, 4)} tableType={SimpleTableType.DOCUMENTS} />*/}
                    {
                        <Table 
                            tableConfig={myTableConfigDocs} 
                            style={{}}
                            onColumnSorting={(event:Event)=>{
                                //console.log(event)
                            }}
                            onInputSearchBarChange={(event:Event)=>{
                                console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                            }}
                            onClickRightAction={(event:Event)=>{
                                //console.log(event)
                            }}
                            onButtonItemCellClick={(event:Event)=>{
                                console.log(event)
                            }}
                            onTextLinkCellClick={(event:Event)=>{
                            }}
                            onClickKebabAction1={(event:Event)=>{
                                navigate(RoutePath.CONTRACTS_EDIT_DOCUMENT + "?entityId=" + id + "&entityType=" + EntityType.DRIVER + "&documentType=" + driverData?.documents[(event as CustomEvent).detail.row].type);
                            }}
                            onClickKebabAction2={(event:Event)=>{
                                downloadDriverDocument(driverData?.documents[(event as CustomEvent).detail.row].type as DocumentType);
                            }}
                            onClickKebabAction3={(event:Event)=>{ //elimina documento
                                deleteSingleDocument(
                                    id!,
                                    EntityType.DRIVER,
                                    driverData?.documents[(event as CustomEvent).detail.row].type,
                                    {
                                        "type": driverData?.documents[(event as CustomEvent).detail.row].type as DocumentType,
                                        "name": DocumentType.EMPTY_FILE,
                                        "size": 0,
                                        "content": "",
                                        "expire": ULTRA_LATE_DATE,
                                    }
                                );
                            }}
                        />
                    }
                    {documentTableLegend(t('transporter.transporterData.fileTable.tableLegend.2'),t('transporter.transporterData.fileTable.tableLegend.1'),t('transporter.transporterData.fileTable.tableLegend.3'))}
                </div>
            </Col>
        </Row>

        <Row>
            <Col>
                <div className="documentTableTitle--contract">
                    {/*Certificazioni*/}
                    {t('drivers.driverData.fileTable.title.1')}
                </div>
                <div className="listTableContainer" style={{marginTop: "24px" }}>
                    {/*<SimpleTable id="simpleTable_2" columns={tableCols} rows={tableRows(4)} tableType={SimpleTableType.DOCUMENTS} />*/}
                    {
                        <Table 
                            tableConfig={myTableConfigCert} 
                            style={{}}
                            onColumnSorting={(event:Event)=>{
                                //console.log(event)
                            }}
                            onInputSearchBarChange={(event:Event)=>{
                            }}
                            onClickRightAction={(event:Event)=>{
                            }}
                            onButtonItemCellClick={(event:Event)=>{
                            }}
                            onTextLinkCellClick={(event:Event)=>{
                            }}
                            onClickKebabAction1={(event:Event)=>{
                                navigate(RoutePath.CONTRACTS_EDIT_DOCUMENT + "?entityId=" + id + "&entityType=" + EntityType.DRIVER + "&documentType=" + driverData?.documents[(event as CustomEvent).detail.row+4].type);
                            }}
                            onClickKebabAction2={(event:Event)=>{
                                downloadDriverDocument(driverData?.documents[(event as CustomEvent).detail.row+4].type as DocumentType);
                            }}
                            
                        />
                    }
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "24px" }}>
                        {documentTableLegend(t('transporter.transporterData.fileTable.tableLegend.2'),t('transporter.transporterData.fileTable.tableLegend.1'))}
                    </div>
                </div>
            </Col>
        </Row>

        {/** Delete confirm */}
        {showConfirm &&
            <ConfirmPopUp
                title={t('drivers.driverData.deleteConfirm.title')}//"Sei sicuro di voler eliminare l'autista?"
                description={t('drivers.driverData.deleteConfirm.description')}//"L’azione è irreversibile e dovrai inserire ex novo tutte le informazioni sull'autista."
                onClose={() => {
                    setShowConfirm(false);
                }}
                onConfirm={() => {
                    setShowConfirm(false);
                    deleteDriver();
                }} />
        }


        {command && (command === UICommand.REJECT || command === UICommand.REJECT_CREDIT) &&
            <DialogMessage
                title={t((command === UICommand.REJECT ? 'drivers.driverData.rejectDialog.reject' : 'drivers.driverData.rejectDialog.rejectCredit') + ".title")}
                subtitle={t((command === UICommand.REJECT ? 'drivers.driverData.rejectDialog.reject' : 'drivers.driverData.rejectDialog.rejectCredit') + ".subTitle")}
                textareaLabel={t('drivers.driverData.rejectDialog.textareaLabel')}
                textAreaPlaceholder={t('drivers.driverData.rejectDialog.txtAreaPlaceholder')}
                textareaDescription={t('drivers.driverData.rejectDialog.textareaDescription')}
                submitButtonLabel={t('drivers.driverData.rejectDialog.submitButtonLabel')}
                icon={''}
                type={EntityType.DRIVER}
                onClose={() => setCommand(undefined)}
                onClick={(message: string) => {
                    console.log(driverData)
                    if (driverData?.driverId) {
                        changeEntityStatus(
                            command === UICommand.REJECT ? BadgeStatus.NOTAPPROVED : BadgeStatus.NOT_CREDITED,
                            message
                        )
                    }
                }}
            />
        }

    </div>)
}

export default DriverData;